import {ValidatableEmail} from 'Core/models/Email';
import {ValidatablePhone} from 'Core/models/PhoneNumber';
import {ValidatableNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import * as I from 'immutable';
import {createAction} from 'Shared/utilities/actionCreator';
import agentSupportUserType, {
  AgentSupportUserTypeAction,
  AgentSupportUserTypeActionType,
} from 'Users/ducks/agentSupportUserType';
import agentUserTypeReducer, {AgentUserTypeAction, AgentUserTypeActionType} from 'Users/ducks/agentUserType';
import vipUserType, {VIPUserTypeAction, VipUserTypeActionType} from 'Users/ducks/vipUserType';
import {LineOfBusiness} from 'Users/models/LineOfBusiness';
import {EmptyAgentSupportUserType} from 'Users/models/User/Agent/AgentSupportUserType';
import {EmptyAgentUserType} from 'Users/models/User/Agent/AgentUserType';
import {DefaultVIPUserType} from 'Users/models/User/Agent/VipUserType';
import {
  AgentRole,
  AgentUserType,
  InvalidAgentUserType,
  matchAgentUserType,
  UndeterminedAgentUserType,
  ValidAgentUserType,
} from 'Users/models/User/AgentUserType';
import {AccountType} from 'Users/models/User/BasicInformation';
import {AdminUserType as MessagingAdminUserType} from 'Users/models/User/Messaging/AdminUserType';
import {ManagerUserType as MessagingManagerUserType} from 'Users/models/User/Messaging/ManagerUserType';
import {MessageSenderUserType} from 'Users/models/User/Messaging/MessageSenderUserType';
import * as U from 'Users/models/User/User';
import {ValidUser} from 'Users/models/User/User';
import {
  InvalidUserType,
  InvalidUserTypeValue,
  matchUserTypeByLineOfBusiness,
  UserType,
  ValidUserType,
  ValidUserTypeValue,
} from 'Users/models/User/UserType';
import {MessagingRole, InvalidMessagingUserType, ValidMessagingUserType} from 'Users/models/User/MessagingUserType';
import {MessageAnalystUserType} from 'Users/models/User/Messaging/MessageAnalystUserType';
import {DefaultIAMUserType} from 'Users/models/User/Agent/IamUserType';
import iamUserType, {IAMUserTypeAction} from './iamUserType';
import {QuotingRole} from 'Users/models/User/Quoting/QuotingRole';
import {InvalidQuotingUserType, ValidQuotingUserType} from 'Users/models/User/QuotingUserType';
import {QuotingAdminUserType} from 'Users/models/User/Quoting/QuotingAdminUserType';

export enum UserEntryActionType {
  ADD_USER_TYPE = 'userEntry/ADD_USER_TYPE',
  REMOVE_USER_TYPE = 'userEntry/REMOVE_USER_TYPE',
  GET_USER_DETAIL = 'userEntry/GET_USER_DETAIL',
  CLEAR_STATE = 'userEntry/CLEAR_STATE',
  CHANGE_AGENT_ROLE = 'userEntry/CHANGE_AGENT_ROLE',
  CHANGE_MESSAGING_ROLE = 'userEntry/CHANGE_MESSAGING_ROLE',
  CHANGE_QUOTING_ROLE = 'userEntry/CHANGE_QUOTING_ROLE',
  CHANGE_ACCOUNT_TYPE = 'userEntry/CHANGE_ACCOUNT_TYPE',
  CHANGE_EMAIL = 'userEntry/CHANGE_EMAIL',
  CHANGE_FIRST_NAME = 'userEntry/CHANGE_FIRST_NAME',
  CHANGE_LAST_NAME = 'userEntry/CHANGE_LAST_NAME',
  CHANGE_PHONE = 'userEntry/CHANGE_PHONE',
  CHANGE_USER_NAME = 'userEntry/CHANGE_USER_NAME',
  VALIDATE_EMAIL = 'userEntry/VALIDATE_EMAIL',
  VALIDATE_PHONE = 'userEntry/VALIDATE_PHONE',
  VALIDATE_USERNAME = 'userEntry/VALIDATE_USERNAME',
  ADD_USER = 'userEntry/ADD_USER',
  UPDATE_USER = 'userEntry/UPDATE_USER',
  SHOW_ERROR = 'userEntry/SHOW_ERROR',
  CHANGE_IS_USER_SUPPORT = 'userEntry/CHANGE_IS_USER_SUPPORT',
  RESET_USER_TYPE_STATE = 'userEntry/RESET_USER_TYPE_STATE',
  CHANGE_IS_USER_DISABLE = 'userEntry/CHANGE_IS_USER_DISABLE',
}

const reverseEnum = <TEnum>(enumeration: TEnum) =>
  Object.entries(enumeration)
    .map(([key, value]) => ({[value]: key}))
    .reduce((prev, curr) => ({...prev, ...curr}));

const isAgentSupportUserTypeAction = (action: UserEntryAction): action is AgentSupportUserTypeAction => {
  const {type} = action;

  return type in reverseEnum(AgentSupportUserTypeActionType);
};

const isVipUserTypeAction = (action: UserEntryAction): action is VIPUserTypeAction => {
  const {type} = action;

  return type in reverseEnum(VipUserTypeActionType);
};

const isAgentUserTypeAction = (action: UserEntryAction): action is AgentUserTypeAction => {
  const {type} = action;

  return type in reverseEnum(AgentUserTypeActionType);
};

export const changeAccountType = (accountType: AccountType) =>
  createAction(UserEntryActionType.CHANGE_ACCOUNT_TYPE, {accountType});
export const changeFirstName = (firstName: string) => createAction(UserEntryActionType.CHANGE_FIRST_NAME, {firstName});
export const changeLastName = (lastName: string) => createAction(UserEntryActionType.CHANGE_LAST_NAME, {lastName});
export const changeEmail = (email: ValidatableEmail) => createAction(UserEntryActionType.CHANGE_EMAIL, {email});
export const changeUserName = (username: ValidatableNonEmptyString) =>
  createAction(UserEntryActionType.CHANGE_USER_NAME, {username});
export const changePhoneNumber = (phoneNumber: ValidatablePhone) =>
  createAction(UserEntryActionType.CHANGE_PHONE, {phoneNumber});
export const changeAgentRole = (agentRole: AgentRole) =>
  createAction(UserEntryActionType.CHANGE_AGENT_ROLE, {agentRole});
export const changeQuotingRole = (quotingRole: QuotingRole) =>
  createAction(UserEntryActionType.CHANGE_QUOTING_ROLE, {quotingRole});
export const changeIsUserSupport = (isUserSupport: boolean) =>
  createAction(UserEntryActionType.CHANGE_IS_USER_SUPPORT, {isUserSupport});
  export const changeIsUserDisable = (isDisabled: boolean) =>
    createAction(UserEntryActionType.CHANGE_IS_USER_DISABLE, {isDisabled});
export const changeMessagingRole = (messagingRole: MessagingRole) =>
  createAction(UserEntryActionType.CHANGE_MESSAGING_ROLE, {messagingRole});
export const validateEmail = (email: string) => createAction(UserEntryActionType.VALIDATE_EMAIL, email);
export const validateUserName = (username: string) => createAction(UserEntryActionType.VALIDATE_USERNAME, username);
export const validatePhoneNumber = (phone: string) => createAction(UserEntryActionType.VALIDATE_PHONE, phone);
export const addAccessFromLineOfBusiness = (lineOfBusiness: LineOfBusiness) =>
  createAction(UserEntryActionType.ADD_USER_TYPE, {lineOfBusiness});
export const removeUserType = (userType: ValidUserTypeValue | InvalidUserTypeValue) =>
  createAction(UserEntryActionType.REMOVE_USER_TYPE, {userType});

export const resetUserTypeState = () => createAction(UserEntryActionType.RESET_USER_TYPE_STATE);
export const getUserDetail = (userId: string) => createAction(UserEntryActionType.GET_USER_DETAIL, {userId});
export const addUser = (user: ValidUser) => createAction(UserEntryActionType.ADD_USER, {user});
export const updateUser = (user: ValidUser, userId: string) =>
  createAction(UserEntryActionType.UPDATE_USER, {user, userId});
export const clearState = () => createAction(UserEntryActionType.CLEAR_STATE);
export const showError = (user: ValidUser) => createAction(UserEntryActionType.SHOW_ERROR, {user});

const getUserTypeFromLineOfBusiness = (lineOfBusiness: LineOfBusiness): UserType => {
  switch (lineOfBusiness) {
    case LineOfBusiness.Agent:
      return new InvalidUserType(new InvalidAgentUserType(new UndeterminedAgentUserType()));
    case LineOfBusiness.Messaging:
      return new InvalidUserType(new InvalidMessagingUserType());
    case LineOfBusiness.Quoting:
      return new InvalidUserType(new InvalidQuotingUserType());
    default:
      return new InvalidUserType(new InvalidAgentUserType(new UndeterminedAgentUserType()));
  }
};

type UserEntryState = U.User;
const DEFAULT_STATE = U.EmptyUser;

export type UserEntryAction =
  | ReturnType<typeof updateUser>
  | ReturnType<typeof addUser>
  | ReturnType<typeof getUserDetail>
  | ReturnType<typeof changeAgentRole>
  | ReturnType<typeof changeQuotingRole>
  | ReturnType<typeof validateEmail>
  | ReturnType<typeof changeIsUserSupport>
  | ReturnType<typeof changeIsUserDisable>
  | ReturnType<typeof changeMessagingRole>
  | ReturnType<typeof addAccessFromLineOfBusiness>
  | ReturnType<typeof changeAccountType>
  | ReturnType<typeof changeFirstName>
  | ReturnType<typeof changeLastName>
  | ReturnType<typeof changeUserName>
  | ReturnType<typeof changeEmail>
  | ReturnType<typeof changePhoneNumber>
  | ReturnType<typeof showError>
  | ReturnType<typeof removeUserType>
  | ReturnType<typeof clearState>
  | ReturnType<typeof resetUserTypeState>
  | VIPUserTypeAction
  | IAMUserTypeAction
  | AgentUserTypeAction
  | AgentSupportUserTypeAction;

const updateAgentUserType = (action: UserEntryAction) => (agentUserType: AgentUserType) => {
  const updatedAgentUserType = matchAgentUserType(agentUserType.value, {
    Agent: agent => agentUserTypeReducer(agent, action as AgentUserTypeAction),
    AgentSupport: agentSupport => agentSupportUserType(agentSupport, action as AgentSupportUserTypeAction),
    VIP: vip => vipUserType(vip, action as VIPUserTypeAction),
    IAM: iam => iamUserType(iam, action as IAMUserTypeAction),
    Undetermined: () => agentUserType.value,
  });

  const validatedAgentUserType = ValidAgentUserType.create(updatedAgentUserType);
  return ValidUserType.create(validatedAgentUserType);
};

const initializeAgentByRole = (action: ReturnType<typeof changeAgentRole>) => {
  const {agentRole} = action.payload;

  switch (agentRole) {
    case AgentRole.VIP:
      return ValidUserType.create(ValidAgentUserType.create(DefaultVIPUserType));
    case AgentRole.IAM:
      return ValidUserType.create(ValidAgentUserType.create(DefaultIAMUserType));
    case AgentRole.Agent:
      return ValidUserType.create(ValidAgentUserType.create(EmptyAgentUserType));
    case AgentRole.AgentSupport:
      return ValidUserType.create(ValidAgentUserType.create(EmptyAgentSupportUserType));
    default:
      return new InvalidUserType(new InvalidAgentUserType(new UndeterminedAgentUserType()));
  }
};

const initializeMessagingByRole = (action: ReturnType<typeof changeMessagingRole>) => {
  const {messagingRole} = action.payload;

  switch (messagingRole) {
    case MessagingRole.Admin:
      return ValidUserType.create(new ValidMessagingUserType(new MessagingAdminUserType()));
    case MessagingRole.Manager:
      return ValidUserType.create(new ValidMessagingUserType(new MessagingManagerUserType()));
    case MessagingRole.MessageSender:
      return ValidUserType.create(new ValidMessagingUserType(new MessageSenderUserType()));
    case MessagingRole.MessageAnalyst:
      return ValidUserType.create(new ValidMessagingUserType(new MessageAnalystUserType()));
    default:
      return new InvalidUserType(new InvalidMessagingUserType());
  }
};

const initializeQuotingByRole = (action: ReturnType<typeof changeQuotingRole>) => {
  const {quotingRole} = action.payload;

  if (quotingRole === QuotingRole.Admin) {
    return ValidUserType.create(new ValidQuotingUserType(new QuotingAdminUserType()));
  }

  return new InvalidUserType(new InvalidQuotingUserType());
};

export default (state: UserEntryState = DEFAULT_STATE, action: UserEntryAction): UserEntryState => {
  if (isAgentUserTypeAction(action) || isAgentSupportUserTypeAction(action) || isVipUserTypeAction(action)) {
    const existingUserTypes: I.List<UserType> = state.userTypes;
    const userTypeToUpdate = existingUserTypes.find(ut =>
      matchUserTypeByLineOfBusiness(ut.value, {
        Agent: () => true,
        Default: () => false,
      })
    );

    if (userTypeToUpdate) {
      const updatedUserType = matchUserTypeByLineOfBusiness(userTypeToUpdate.value, {
        Agent: updateAgentUserType(action),
        Default: () => userTypeToUpdate,
      });

      return U.changeUserType(updatedUserType)(state);
    } else {
      return state;
    }
  }

  switch (action.type) {
    case UserEntryActionType.SHOW_ERROR:
      const {user} = action.payload;
      return U.showError(user);
    case UserEntryActionType.UPDATE_USER:
    case UserEntryActionType.ADD_USER:
    case UserEntryActionType.GET_USER_DETAIL:
      return new U.LoadingUser(state.basicInformation, state.userTypes);
    case UserEntryActionType.CHANGE_ACCOUNT_TYPE:
      return U.changeAccountType(action.payload.accountType)(state);
    case UserEntryActionType.CHANGE_FIRST_NAME:
      return U.changeFirstName(action.payload.firstName)(state);
    case UserEntryActionType.CHANGE_LAST_NAME:
      return U.changeLastName(action.payload.lastName)(state);
    case UserEntryActionType.CHANGE_USER_NAME:
      return U.changeUserName(action.payload.username)(state);
    case UserEntryActionType.CHANGE_PHONE:
      return U.changePhoneNumber(action.payload.phoneNumber)(state);
    case UserEntryActionType.CHANGE_EMAIL:
      return U.changeEmail(action.payload.email)(state);
    case UserEntryActionType.CHANGE_IS_USER_DISABLE:
      return U.changeIsUserDisable(action.payload.isDisabled)(state);
    case UserEntryActionType.ADD_USER_TYPE:
      const {lineOfBusiness} = action.payload;
      const newUserType = getUserTypeFromLineOfBusiness(lineOfBusiness);

      return U.addUserType(newUserType)(state);
    case UserEntryActionType.REMOVE_USER_TYPE:
      return U.removeUserType(ValidUserType.create(action.payload.userType))(state);
    case UserEntryActionType.CHANGE_AGENT_ROLE: {
      const existingUserTypes: I.List<UserType> = state.userTypes;
      const userTypeToUpdate = existingUserTypes.find(
        ut => ut.value.key === 'InvalidAgentUserType' || ut.value.key === 'ValidAgentUserType'
      );

      if (userTypeToUpdate) {
        const updatedUserType = matchUserTypeByLineOfBusiness(userTypeToUpdate.value, {
          Agent: () => initializeAgentByRole(action),
          Default: () => userTypeToUpdate,
        });
        return U.changeUserType(updatedUserType)(state);
      } else {
        return state;
      }
    }
    case UserEntryActionType.CHANGE_MESSAGING_ROLE: {
      const existingUserTypes: I.List<UserType> = state.userTypes;
      const userTypeToUpdate = existingUserTypes.find(
        ut => ut.value.key === 'InvalidMessagingUserType' || ut.value.key === 'ValidMessagingUserType'
      );

      if (userTypeToUpdate) {
        const updatedUserType = matchUserTypeByLineOfBusiness(userTypeToUpdate.value, {
          Messaging: () => initializeMessagingByRole(action),
          Default: () => userTypeToUpdate,
        });
        return U.changeUserType(updatedUserType)(state);
      } else {
        return state;
      }
    }

    case UserEntryActionType.CHANGE_QUOTING_ROLE: {
      const existingUserTypes: I.List<UserType> = state.userTypes;
      const userTypeToUpdate = existingUserTypes.find(
        ut => ut.value.key === 'InvalidQuotingUserType' || ut.value.key === 'ValidQuotingUserType'
      );

      if (userTypeToUpdate) {
        const updatedUserType = matchUserTypeByLineOfBusiness(userTypeToUpdate.value, {
          Quoting: () => initializeQuotingByRole(action),
          Default: () => userTypeToUpdate,
        });
        return U.changeUserType(updatedUserType)(state);
      } else {
        return state;
      }
    }

    case UserEntryActionType.CHANGE_IS_USER_SUPPORT: {
      return U.changeIsUserSupport(action.payload.isUserSupport)(state);
    }
   
    case UserEntryActionType.CLEAR_STATE:
      return DEFAULT_STATE;

    case UserEntryActionType.RESET_USER_TYPE_STATE: {
      return U.resetUserType(state.basicInformation)
    }
    default:
      return state;
  }
};
